/**
 * Core types for the unified translation system.
 * These types are framework-agnostic and can be used in any TypeScript project.
 */

/**
 * Status of a translatable field.
 */
export type TranslationStatus = 'pending' | 'translating' | 'translated' | 'error';

/**
 * A single translatable field with original and translated values.
 * This is the atomic unit of translation.
 */
export interface TranslatableField {
	/** Original text before translation */
	original: string;
	/** Translated text (null if not yet translated) */
	translated: string | null;
	/** Current translation status */
	status: TranslationStatus;
}

/**
 * Configuration for extracting and processing a translatable field from source data.
 * @template T - The source data type
 */
export interface FieldConfig<T> {
	/** Unique key identifying this field (e.g., 'name', 'address', 'description') */
	key: string;
	/** Function to extract the field value from the source object */
	extract: (item: T) => string | undefined | null;
	/**
	 * Optional: Pre-process the value before translation.
	 * Useful for splitting multi-line text into separate translation units.
	 * @returns Array of strings to translate separately
	 */
	preprocess?: (value: string) => string[];
	/**
	 * Optional: Post-process translated values back into a single string.
	 * Must be provided if preprocess is used.
	 * @param values - Translated values in same order as preprocess output
	 * @returns Reconstructed translated string
	 */
	postprocess?: (values: string[]) => string;
}

/**
 * Configuration for translating a specific data type.
 * Defines how to extract translatable fields from any data structure.
 * @template T - The source data type (e.g., EventInfo, Product, Article)
 */
export interface TranslationConfig<T> {
	/** Unique name for this configuration (e.g., 'event', 'ticket', 'product') */
	name: string;
	/** Function to extract a unique identifier from the item */
	getId: (item: T) => string | number;
	/** Array of field configurations defining which fields to translate */
	fields: FieldConfig<T>[];
}

/**
 * A request to translate a single piece of text.
 */
export interface TranslationRequest {
	/** Unique key for this translation (used for matching results) */
	key: string;
	/** Original text to translate */
	text: string;
}

/**
 * Result of a translation request.
 */
export interface TranslationResult {
	/** Key matching the original request */
	key: string;
	/** Original text that was translated */
	original: string;
	/** Translated text */
	translated: string;
}

/**
 * Configuration options for the TranslationService.
 */
export interface TranslationServiceConfig {
	/** Google Translate API key */
	apiKey: string;
	/** Target language code (defaults to browser language) */
	targetLanguage?: string;
	/** Maximum items per API request (defaults to 120) */
	chunkSize?: number;
	/** Enable language detection to skip already-translated text (defaults to false) */
	enableDetection?: boolean;
}

/**
 * Statistics about translation operations.
 */
export interface TranslationStats {
	/** Total characters translated in current session */
	totalCharacters: number;
	/** Number of cache hits (avoided API calls) */
	cacheHits: number;
	/** Number of API calls made */
	apiCalls: number;
	/** Whether the last translation used the API (vs cache only) */
	lastTranslationUsedApi: boolean;
}

/**
 * Callback type for translation progress events.
 */
export type TranslationProgressCallback = (progress: {
	completed: number;
	total: number;
	percentage: number;
}) => void;

/**
 * Error thrown by translation operations.
 */
export class TranslationError extends Error {
	constructor(
		message: string,
		public readonly code: 'API_ERROR' | 'NETWORK_ERROR' | 'INVALID_CONFIG' | 'RATE_LIMIT',
		public readonly originalError?: Error,
	) {
		super(message);
		this.name = 'TranslationError';
	}
}
